import type { ButtonHTMLAttributes, ReactNode, Ref } from "react";

import "./motion/pixel-motion.css";

import { cx } from "@/lib/cx";
import { Spinner } from "./Spinner";
import { pressDim } from "./tones";

/**
 * The Material button ladder. **Nothing here is outlined**: an action is a
 * fill, a tonal surface, or a text/ghost treatment.
 *
 * Legacy variant names remain stable for call sites, but every name resolves
 * to the one shared Material grammar:
 *
 * | name           | iOS style   | fill              | label              |
 * |----------------|-------------|-------------------|--------------------|
 * | `primary`      | filled      | `bg-accent-ink`   | `accent-ink-on`    |
 * | `prominent`    | prominent   | `bg-accent-fill`  | `accent-fill-on`   |
 * | `tinted`       | tinted      | `bg-accent-soft`  | `accent-ink`       |
 * | `secondary`    | gray        | `bg-fill-3`       | `ink`              |
 * | `ghost`        | plain, quiet| none              | `muted` → `ink`    |
 * | `tonal-ghost`  | tonal hover | none → `fill-3`   | `muted` → `ink`    |
 * | `plain`        | plain       | none              | `accent-ink`       |
 * | `danger`       | destructive | `bg-danger`       | `danger-on`        |
 * | `danger-plain` | destructive | none              | `danger`           |
 *
 * **The primary button keeps the legacy accent-ink pair.** `prominent` uses
 * the theme-adaptive solid accent pair: deep teal + near-white in light, vivid
 * teal + dark ink in dark. Both compact labels pass AA without making the dark
 * selection disappear into its segmented track.
 */
export type ButtonVariant =
  | "primary"
  | "prominent"
  | "tinted"
  | "secondary"
  | "ghost"
  | "tonal-ghost"
  | "plain"
  | "danger"
  | "danger-plain";

export type ButtonSize = "fit" | "xs" | "sm" | "md" | "lg";

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Ordinary buttons are pills; row-like shell controls may use the control rung. */
  corners?: "pill" | "control";
  /** Shows a spinner and blocks further clicks. */
  loading?: boolean;
  /** Leading glyph or icon. */
  icon?: ReactNode;
  fullWidth?: boolean;
  /** Keeps labelled toolbar/menu triggers left-aligned without class overrides. */
  align?: "center" | "start";
  /**
   * A square, text-free button. Pass `aria-label` — there is nothing to read.
   * Exists because six call sites were doing `className="h-8 w-8 px-0"` and
   * three of them had forgotten the label.
   */
  iconOnly?: boolean;
  /**
   * The underlying `<button>`.
   *
   * React 19 passes `ref` as an ordinary prop, so this needs no `forwardRef`.
   * It exists because callers that must *place focus* — `Dialog`'s
   * `initialFocus`, a form putting the cursor back on the control that failed —
   * were reduced to wrapping the button in `<span className="contents">` just
   * to have something to hold.
   */
  ref?: Ref<HTMLButtonElement>;
};

/**
 * Disabled is a state of its own, not the enabled style at 45% opacity.
 *
 * `opacity-45` over the accent produced a washed-out sage — saturated enough to
 * still read as the primary action, dim enough to read as a rendering fault
 * (`UI-REVIEW.md` §3 "Minor"). `IOS-DESIGN.md` §5.1 asks for "opacity-40 plus a
 * neutral fill", and the two halves of that apply to different shapes:
 *
 * - a button that **has** a fill is repainted to the neutral `bg-fill-4`, which
 *   is the same inert grey for all eight variants rather than eight different
 *   washed-out accents;
 * - a button that has **no** fill has nothing to repaint, so it takes the
 *   opacity instead.
 *
 * Both land on `text-faint`, which fails AA deliberately: WCAG exempts disabled
 * controls and anything more legible reads as live.
 */
const DISABLED =
  "disabled:pointer-events-none disabled:text-faint disabled:shadow-none disabled:bg-fill-3 " +
  "disabled:border-ps1-line";

/** Plain variants keep no fill when disabled, so they fade instead. */
const DISABLED_PLAIN =
  "disabled:pointer-events-none disabled:text-faint disabled:shadow-none " +
  "disabled:bg-transparent disabled:opacity-40";

/**
 * The 44px floor, on touch only.
 *
 * Expressed as `min-h` / `min-w` rather than `h` / `w` deliberately: a second
 * `height` utility would collide with the size's own and be resolved by
 * Tailwind's sheet order rather than by intent, whereas a minimum simply wins.
 * Row actions were 32×32 and the drag handle 20×28 — both unusable with a
 * thumb, which is the other half of why the phone had no row actions.
 */
const TAP = "pointer-coarse:min-h-tap";

/**
 * Pressed is opacity, never a border and never a size change — `IOS-DESIGN.md`
 * §5.1. `transition-opacity` rather than `transition-colors`, so the press
 * feedback cannot accidentally animate a *selection* colour on a control that
 * is also a selected thing.
 */
const BASE =
  "relative inline-flex items-center gap-2 tracking-[0.01em] select-none " +
  pressDim +
  " " +
  TAP;

// PS1 skin: primary is the gold command with dark ink, secondary an orange
// frame, ghost an orange frame only under the pointer, danger the red one.
const VARIANTS: Record<ButtonVariant, string> = {
  // 5.61 light / 7.23 dark. The one primary action on a screen.
  primary: "border-2 border-[#ffe08a] bg-ps1-gold-fill text-ps1-gold-ink hover:bg-[#ffd45e]",
  // Deep teal + near-white in light (5.61); vivid teal + dark ink in dark (8.67).
  prominent: "border-2 border-[#ffe08a] bg-ps1-gold-fill text-ps1-gold-ink hover:bg-[#ffd45e]",
  // 4.60 light / 5.01 dark. A secondary action standing next to a filled one.
  tinted: "border-2 border-ps1-gold bg-accent-soft text-ps1-gold hover:bg-hover",
  // iOS "gray": a neutral fill, not a hairline box. `ink` on a fill tier is
  // >= 9.70 in both themes.
  secondary: "border-2 border-ps1-orange bg-ps1-panel text-ink hover:border-ps1-gold hover:bg-hover",
  // The quiet plain button: an ✕, an overflow ⋯, a row action. It shares the
  // neutral row/ghost hover recipe rather than inventing a private wash.
  ghost: "border-2 border-transparent text-muted hover:border-ps1-orange hover:bg-hover hover:text-ink",
  // Segments, calendar cells and chips hover within an existing tonal field.
  "tonal-ghost": "text-muted hover:bg-hover hover:text-ink",
  // The loud plain button: a nav-bar action, "Show 22 more".
  plain: "text-ps1-gold hover:bg-hover",
  danger: "border-2 border-ps1-red bg-ps1-red text-white hover:bg-[#ff4752]",
  "danger-plain": "text-danger hover:bg-danger-soft",
};

/** Which variants have no fill of their own, and so fade when disabled. */
const PLAIN_VARIANTS = new Set<ButtonVariant>(["ghost", "tonal-ghost", "plain", "danger-plain"]);

/**
 * Height, padding and type. Ordinary controls use the shared 32 / 40 / 48px
 * rungs. `xs` is the deliberate 28px exception for narrow status/chrome rows.
 */
const SIZES: Record<ButtonSize, string> = {
  fit: "text-label",
  xs: "h-7 px-2 text-caption",
  sm: "h-8 px-3 text-label",
  md: "h-10 px-4 text-label",
  lg: "h-12 px-6 text-label",
};

/**
 * Square footprints, matched to the same three heights. These *replace* the
 * entries above rather than overriding them: two padding utilities on one
 * element are resolved by Tailwind's own sheet order, not by the order they
 * appear in the class attribute, so `px-0 px-3` is not a reliable override.
 */
const ICON_SIZES: Record<ButtonSize, string> = {
  fit: "size-8 text-caption pointer-coarse:min-w-tap",
  xs: "size-7 text-caption pointer-coarse:min-w-tap",
  sm: "size-8 text-caption pointer-coarse:min-w-tap",
  md: "size-10 text-label pointer-coarse:min-w-tap",
  lg: "size-10 text-label pointer-coarse:min-w-tap",
};

export function Button({
  variant = "secondary",
  size = "md",
  corners = "pill",
  loading = false,
  icon,
  fullWidth = false,
  align = "center",
  iconOnly = false,
  className,
  disabled,
  children,
  type = "button",
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      data-variant={variant}
      className={cx(
        BASE,
        "busee-btn busee-press",
        // PS1: every button is square; `corners` stays for source compatibility.
        "rounded-control",
        VARIANTS[variant],
        PLAIN_VARIANTS.has(variant) ? DISABLED_PLAIN : DISABLED,
        iconOnly ? ICON_SIZES[size] : SIZES[size],
        iconOnly || align === "center" ? "justify-center" : "justify-start text-left",
        fullWidth && "w-full",
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size="sm" /> : icon}
      {children}
    </button>
  );
}
